(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const finePointer = window.matchMedia(
    "(hover: hover) and (pointer: fine) and (min-width: 990px)",
  );
  const runwayMotion = window.matchMedia(
    "(min-width: 990px) and (min-height: 861px)",
  );
  const homeControllers = new Map();
  let headerEventsBound = false;
  let headerFrame = 0;
  let manuallyPaused = false;

  function collect(scope, selector) {
    const matches = [];
    if (scope?.matches?.(selector)) matches.push(scope);
    matches.push(...(scope?.querySelectorAll?.(selector) || []));
    return matches;
  }

  function motionAllowed(home) {
    return (
      home?.dataset.ttMotion !== "false" &&
      !reduceMotion.matches &&
      document.documentElement.dataset.ttMotionPaused !== "true" &&
      !window.Shopify?.designMode
    );
  }

  function finishReveals(home) {
    home
      .querySelectorAll("[data-tt-reveal]")
      .forEach((item) => item.classList.add("is-visible"));
  }

  function trackAnimation(controller, animation) {
    controller.animations.add(animation);
    animation.finished
      .catch(() => {})
      .finally(() => controller.animations.delete(animation));
  }

  function initReveals(controller) {
    const { home, signal } = controller;
    const items = [...home.querySelectorAll("[data-tt-reveal]")];

    items.forEach((item) => item.style.setProperty("--tt-reveal-delay", "0ms"));
    home.querySelectorAll("[data-tt-reveal-group]").forEach((group) => {
      group.querySelectorAll("[data-tt-reveal]").forEach((item, index) => {
        item.style.setProperty(
          "--tt-reveal-delay",
          `${Math.min(index, 3) * 70}ms`,
        );
      });
    });

    items
      .filter((item) => item.closest(".tt-hero"))
      .forEach((item) => item.classList.add("is-visible"));

    if (
      !motionAllowed(home) ||
      home.dataset.ttRevealEnabled === "false" ||
      !("IntersectionObserver" in window)
    ) {
      finishReveals(home);
      return;
    }

    home.classList.add("tt-reveal-ready");
    controller.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          controller.revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );

    items
      .filter((item) => !item.classList.contains("is-visible"))
      .forEach((item) => controller.revealObserver.observe(item));

    home.addEventListener(
      "focusin",
      (event) => {
        const item = event.target.closest("[data-tt-reveal]");
        if (!item || !home.contains(item)) return;
        item.classList.add("is-visible");
        controller.revealObserver?.unobserve(item);
      },
      { capture: true, signal },
    );
  }

  function assembleHero(controller) {
    if (!motionAllowed(controller.home) || !Element.prototype.animate) return;

    const plans = [
      [
        ".tt-hero-brand-scene__mascot",
        [
          { translate: "0 1.8rem", scale: "0.97" },
          { translate: "0 0", scale: "1" },
        ],
        0,
      ],
      [
        ".tt-hero-look--1 .tt-hero-look__inner",
        [
          { translate: "-2rem 2.4rem", rotate: "-2deg" },
          { translate: "0 0", rotate: "0deg" },
        ],
        90,
      ],
      [
        ".tt-hero-look--2 .tt-hero-look__inner",
        [
          { translate: "2rem 2.4rem", rotate: "2deg" },
          { translate: "0 0", rotate: "0deg" },
        ],
        150,
      ],
      [
        ".tt-hero__seal",
        [
          { translate: "1.4rem -1rem", rotate: "4deg" },
          { translate: "0 0", rotate: "0deg" },
        ],
        210,
      ],
      [
        ".tt-hero-brand-scene__flash",
        [{ translate: "1rem 0" }, { translate: "0 0" }],
        240,
      ],
    ];

    plans.forEach(([selector, keyframes, delay]) => {
      const element = controller.home.querySelector(selector);
      if (!element) return;
      const animation = element.animate(keyframes, {
        duration: 620,
        delay,
        easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
        fill: "backwards",
      });
      trackAnimation(controller, animation);
    });
  }

  function initHeroDepth(controller) {
    const visual = controller.home.querySelector("[data-tt-hero-visual]");
    if (!visual) return;

    const layers = {
      mascot: visual.querySelector(".tt-hero-brand-scene__mascot"),
      first: visual.querySelector(".tt-hero-look--1 .tt-hero-look__inner"),
      second: visual.querySelector(".tt-hero-look--2 .tt-hero-look__inner"),
      seal: visual.querySelector(".tt-hero__seal"),
    };
    let pointer;
    let frame = 0;

    const reset = () => {
      Object.values(layers).forEach((layer) =>
        layer?.style.removeProperty("translate"),
      );
    };

    const draw = () => {
      frame = 0;
      if (!pointer || !motionAllowed(controller.home) || !finePointer.matches) {
        reset();
        return;
      }

      const bounds = visual.getBoundingClientRect();
      const x = ((pointer.clientX - bounds.left) / bounds.width - 0.5) * 12;
      const y = ((pointer.clientY - bounds.top) / bounds.height - 0.5) * 12;
      if (layers.mascot)
        layers.mascot.style.translate = `${x * 0.22}px ${y * 0.22}px`;
      if (layers.first)
        layers.first.style.translate = `${x * -0.48}px ${y * -0.38}px`;
      if (layers.second)
        layers.second.style.translate = `${x * 0.52}px ${y * 0.42}px`;
      if (layers.seal)
        layers.seal.style.translate = `${x * 0.28}px ${y * -0.28}px`;
    };

    const schedule = (event) => {
      pointer = event;
      if (!frame) frame = requestAnimationFrame(draw);
    };

    visual.addEventListener("pointerenter", schedule, {
      signal: controller.signal,
    });
    visual.addEventListener("pointermove", schedule, {
      signal: controller.signal,
    });
    visual.addEventListener(
      "pointerleave",
      () => {
        pointer = null;
        reset();
      },
      { signal: controller.signal },
    );

    controller.cleanups.push(() => {
      if (frame) cancelAnimationFrame(frame);
      reset();
    });
  }

  function initRunway(controller) {
    const runway = controller.home.querySelector("[data-tt-runway]");
    if (!runway) return;

    const progressLabel = runway.querySelector("[data-tt-runway-progress]");
    let frame = 0;

    const setStatic = () => {
      runway.classList.remove("is-motion-active");
      runway.style.setProperty("--tt-runway-left-x", "0rem");
      runway.style.setProperty("--tt-runway-left-y", "0rem");
      runway.style.setProperty("--tt-runway-right-x", "0rem");
      runway.style.setProperty("--tt-runway-right-y", "0rem");
      runway.style.setProperty("--tt-runway-mascot-y", "0rem");
      runway.style.setProperty("--tt-runway-mascot-rotate", "-3deg");
      runway.style.setProperty("--tt-runway-meter", "100%");
      if (progressLabel) progressLabel.textContent = "100";
    };

    const update = () => {
      frame = 0;
      if (!motionAllowed(controller.home) || !runwayMotion.matches) {
        setStatic();
        return;
      }

      const bounds = runway.getBoundingClientRect();
      runway.classList.toggle(
        "is-motion-active",
        bounds.bottom > 0 && bounds.top < window.innerHeight,
      );
      const distance = Math.max(runway.offsetHeight - window.innerHeight, 1);
      const progress = Math.min(Math.max(-bounds.top / distance, 0), 1);
      const leftX = -64 + progress * 100;
      const leftY = 24 - progress * 40;
      const rightX = 64 - progress * 104;
      const rightY = -16 + progress * 38;
      const mascotY = Math.sin(progress * Math.PI) * -20;

      runway.style.setProperty("--tt-runway-left-x", `${leftX}px`);
      runway.style.setProperty("--tt-runway-left-y", `${leftY}px`);
      runway.style.setProperty("--tt-runway-right-x", `${rightX}px`);
      runway.style.setProperty("--tt-runway-right-y", `${rightY}px`);
      runway.style.setProperty("--tt-runway-mascot-y", `${mascotY}px`);
      runway.style.setProperty(
        "--tt-runway-mascot-rotate",
        `${-3 + progress * 6}deg`,
      );
      runway.style.setProperty("--tt-runway-meter", `${progress * 100}%`);
      if (progressLabel)
        progressLabel.textContent = String(Math.round(progress * 100)).padStart(
          2,
          "0",
        );
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", schedule, {
      passive: true,
      signal: controller.signal,
    });
    window.addEventListener("resize", schedule, {
      passive: true,
      signal: controller.signal,
    });
    controller.refreshers.push(schedule);
    controller.cleanups.push(() => {
      if (frame) cancelAnimationFrame(frame);
      setStatic();
    });
    schedule();
  }

  function mountHomes(scope) {
    collect(scope, "[data-tt-home]").forEach((home) => {
      if (homeControllers.has(home)) return;

      const abortController = new AbortController();
      const controller = {
        home,
        abortController,
        signal: abortController.signal,
        animations: new Set(),
        cleanups: [],
        refreshers: [],
      };
      homeControllers.set(home, controller);

      initReveals(controller);
      assembleHero(controller);
      initHeroDepth(controller);
      initRunway(controller);
    });
  }

  function destroyHomes(scope) {
    collect(scope, "[data-tt-home]").forEach((home) => {
      const controller = homeControllers.get(home);
      if (!controller) return;

      controller.revealObserver?.disconnect();
      controller.abortController.abort();
      controller.animations.forEach((animation) => animation.cancel());
      controller.cleanups.forEach((cleanup) => cleanup());
      homeControllers.delete(home);
    });
  }

  function initLocalization(scope) {
    collect(scope, "[data-tt-localization-form]").forEach((form) => {
      if (form.dataset.ttLocalizationInit === "true") return;
      form.dataset.ttLocalizationInit = "true";
      form
        .querySelector("select")
        ?.addEventListener("change", () => form.requestSubmit());
    });
  }

  function updateHeaders() {
    headerFrame = 0;
    document.querySelectorAll(".tt-header-shell").forEach((header) => {
      header.classList.toggle("is-scrolled", window.scrollY > 24);
    });
  }

  function initHeaders(scope) {
    collect(scope, ".tt-header-shell").forEach((header) => {
      header.dataset.ttHeaderInit = "true";
    });
    updateHeaders();

    if (headerEventsBound) return;
    headerEventsBound = true;

    window.addEventListener(
      "scroll",
      () => {
        if (!headerFrame) headerFrame = requestAnimationFrame(updateHeaders);
      },
      { passive: true },
    );

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      document
        .querySelectorAll(
          ".tt-mobile-menu[open], .tt-desktop-nav__disclosure[open]",
        )
        .forEach((menu) => menu.removeAttribute("open"));
    });

    document.addEventListener("click", (event) => {
      document.querySelectorAll(".tt-mobile-menu[open]").forEach((menu) => {
        const followedMenuLink =
          event.target.closest("a") && menu.contains(event.target);
        if (followedMenuLink || !menu.contains(event.target))
          menu.removeAttribute("open");
      });
      document
        .querySelectorAll(".tt-desktop-nav__disclosure[open]")
        .forEach((menu) => {
          if (!menu.contains(event.target)) menu.removeAttribute("open");
        });
    });
  }

  function applyMotionState() {
    const paused = manuallyPaused || reduceMotion.matches;
    if (paused) document.documentElement.dataset.ttMotionPaused = "true";
    else delete document.documentElement.dataset.ttMotionPaused;
    document.querySelectorAll("[data-tt-motion-toggle]").forEach((button) => {
      button.setAttribute("aria-pressed", String(paused));
      button.disabled = reduceMotion.matches;
    });

    homeControllers.forEach((controller) => {
      if (paused) {
        finishReveals(controller.home);
        controller.animations.forEach((animation) => animation.cancel());
        controller.home
          .querySelectorAll(
            ".tt-hero-brand-scene__mascot, .tt-hero-look__inner, .tt-hero__seal",
          )
          .forEach((layer) => {
            layer.style.removeProperty("translate");
          });
      }
      controller.refreshers.forEach((refresh) => refresh());
    });
  }

  function initMotionToggles(scope) {
    collect(scope, "[data-tt-motion-toggle]").forEach((button) => {
      if (button.dataset.ttMotionToggleInit === "true") return;
      button.dataset.ttMotionToggleInit = "true";
      button.addEventListener("click", () => {
        if (reduceMotion.matches) return;
        manuallyPaused = !manuallyPaused;
        applyMotionState();
      });
    });
    applyMotionState();
  }

  function init(scope = document) {
    initMotionToggles(scope);
    mountHomes(scope);
    initLocalization(scope);
    initHeaders(scope);
  }

  reduceMotion.addEventListener?.("change", applyMotionState);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) =>
    init(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) =>
    destroyHomes(event.target),
  );
  document.addEventListener("shopify:section:reorder", () => {
    homeControllers.forEach((controller) =>
      controller.refreshers.forEach((refresh) => refresh()),
    );
  });
  document.addEventListener("shopify:section:select", (event) => {
    collect(event.target, "[data-tt-home]").forEach(finishReveals);
  });
})();
